import React from 'react';
import { Link } from 'react-router-dom';
import AnimatedSlider from '../components/AnimatedSlider';
import AnimatedStats from '../components/AnimatedStats';
import AnimatedFeatures from '../components/AnimatedFeatures';
import { Helmet } from 'react-helmet-async';



const highlights = [
    {
        icon: (
            <svg className="h-10 w-10 text-brand" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 1.343-3 3 0 1.657 1.343 3 3 3s3-1.343 3-3c0-1.657-1.343-3-3-3zm0 0V4m0 7v9m0 0H7m5 0h5" /></svg>
        ),
        title: 'Vision',
        desc: 'Transforming well-being, innovation, and sustainable development for thriving communities.'
    },
    {
        icon: (
            <svg className="h-10 w-10 text-brand" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 1.343-3 3 0 1.657 1.343 3 3 3s3-1.343 3-3c0-1.657-1.343-3-3-3zm0 0V4m0 7v9m0 0H7m5 0h5" /></svg>
        ),
        title: 'Mission',
        desc: 'Bridging gaps in healthcare and essential services with patient-centered, tech-enabled solutions.'
    },
    {
        icon: (
            <svg className="h-10 w-10 text-brand" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
        ),
        title: 'Values',
        desc: 'Purity of Purpose, Care Without Compromise, Innovation with Integrity.'
    }
];

const Home = () => (
    <>
        <Helmet>
            <title>PUREMEDIFY LIMITED | Gifted by Nature, Refined for Wellness</title>
            <meta name="description" content="Blending nature’s wisdom with human ingenuity to create holistic, life-enhancing solutions in healthcare, wellness, and community impact." />
            <meta property="og:title" content="PUREMEDIFY LIMITED | Gifted by Nature, Refined for Wellness" />
            <meta property="og:description" content="Blending nature’s wisdom with human ingenuity to create holistic, life-enhancing solutions in healthcare, wellness, and community impact." />
            <meta property="og:type" content="website" />
            <meta property="og:image" content="https://puremedify.com/og-image.png" />
            <meta property="og:url" content="https://puremedify.com/" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="PUREMEDIFY LIMITED | Gifted by Nature, Refined for Wellness" />
            <meta name="twitter:description" content="Blending nature’s wisdom with human ingenuity to create holistic, life-enhancing solutions in healthcare, wellness, and community impact." />
            <meta name="twitter:image" content="https://puremedify.com/og-image.png" />
        </Helmet>
        <div className="flex min-h-screen flex-col items-center justify-start bg-gradient-to-br from-blue-50 to-teal-100">
            {/* Hero Section */}
            <section className="relative z-0 isolate flex min-h-[560px] w-full flex-col items-center justify-center overflow-hidden md:min-h-[640px]">
                {/* Background Image */}
                <div className="absolute inset-0 z-0 h-full w-full bg-cover bg-center" style={{ backgroundImage: `url(${process.env.PUBLIC_URL + '/image/puremedify-hero-healthcare-africa.jpg'})` }} />
                {/* Dark Overlay */}
                <div className="absolute inset-0 z-10 h-full w-full bg-black/25" />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 z-20 h-full w-full bg-gradient-to-br from-white/78 via-blue-50/72 to-teal-100/68" />
                {/* Motif SVG background */}
                <svg className="pointer-events-none absolute bottom-0 right-0 z-30 h-64 w-64 select-none opacity-20 md:h-96 md:w-96" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="200" cy="200" rx="180" ry="120" fill="#0087ca" />
                    <ellipse cx="270" cy="250" rx="80" ry="50" fill="#00b937" />
                    <ellipse cx="140" cy="300" rx="60" ry="30" fill="#004487" />
                </svg>
                <svg className="animate-float-slow pointer-events-none absolute left-8 top-16 z-30 h-20 w-20 opacity-15 md:left-16 md:top-24 md:h-28 md:w-28" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="40" fill="#0087ca" /></svg>
                <svg className="animate-float-medium pointer-events-none absolute right-12 top-32 z-30 h-14 w-14 opacity-10 md:h-20 md:w-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="35" fill="#00b937" /></svg>

                <div className="relative z-40 mx-auto flex w-full max-w-4xl flex-col items-center px-4 py-16 text-center md:py-24">
                    {/* Eyebrow */}
                    <span className="animate-fade-in mb-5 inline-flex items-center gap-2 rounded-full border border-lochmara-blue/20 bg-white/80 px-4 py-1.5 font-redhat text-sm font-medium tracking-wide text-congress-blue shadow-sm backdrop-blur-sm md:text-base">
                        <span className="inline-block h-2 w-2 rounded-full bg-malachite-green" />
                        Medically Driven · Mission-Centered
                    </span>

                    {/* Headline */}
                    <h1 className="animate-fade-in mb-3 font-ubuntu text-4xl font-bold leading-tight drop-shadow-sm delay-100 md:text-5xl lg:text-6xl">
                        <span className="text-lochmara-blue">Human-Oriented </span>
                        <span className="text-malachite-green">Healthcare</span>
                    </h1>
                    <p className="animate-fade-in mb-6 font-ubuntu text-xl font-semibold text-congress-blue delay-200 md:text-2xl lg:text-3xl">
                        Built on Ethics, Innovation &amp; Compassion
                    </p>

                    {/* Accent divider */}
                    <div className="animate-fade-in mb-6 flex items-center gap-3 delay-300">
                        <span className="h-px w-12 bg-gradient-to-r from-transparent to-lochmara-blue/60" />
                        <span className="h-2 w-2 rounded-full bg-malachite-green" />
                        <span className="h-px w-12 bg-gradient-to-l from-transparent to-lochmara-blue/60" />
                    </div>

                    {/* Summary copy */}
                    <p className="animate-fade-in mb-6 max-w-2xl font-redhat text-base leading-relaxed text-gray-700 delay-400 md:text-lg">
                        PUREMEDIFY transforms healthcare access from city to countryside — delivering patient-centered care with integrity and dignity, so no one is left behind.
                    </p>

                    {/* Value pills */}
                    <div className="animate-fade-in mb-8 flex flex-wrap justify-center gap-2 delay-500 md:gap-3">
                        {['Ethics', 'Innovation', 'Compassion'].map((value) => (
                            <span
                                key={value}
                                className="rounded-full bg-white/70 px-4 py-1.5 font-redhat text-sm font-medium text-lochmara-blue shadow-sm ring-1 ring-lochmara-blue/10 backdrop-blur-sm md:text-base"
                            >
                                {value}
                            </span>
                        ))}
                    </div>

                    {/* CTAs */}
                    <div className="animate-fade-in flex flex-wrap justify-center gap-3 delay-600 md:gap-4">
                        <Link
                            to="/contact"
                            className="inline-block rounded-full bg-brand px-8 py-3 font-ubuntu text-base font-semibold text-white shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-brand-green md:text-lg"
                        >
                            Book an appointment
                        </Link>
                        <Link
                            to="/contact"
                            className="inline-block rounded-full border-2 border-lochmara-blue bg-white/80 px-6 py-3 font-ubuntu text-base font-semibold text-lochmara-blue shadow-md backdrop-blur-sm transition-transform duration-200 hover:scale-105 hover:border-malachite-green hover:text-malachite-green md:text-lg"
                        >
                            Call a doctor
                        </Link>
                        <Link
                            to="/contact"
                            className="inline-block rounded-full border-2 border-congress-blue/30 bg-white/60 px-6 py-3 font-ubuntu text-base font-semibold text-congress-blue shadow-md backdrop-blur-sm transition-transform duration-200 hover:scale-105 hover:border-brand hover:text-brand md:text-lg"
                        >
                            Call an ambulance
                        </Link>
                    </div>
                </div>

                <style>{`
                    @keyframes fade-in { 0% { opacity: 0; transform: translateY(24px); } 100% { opacity: 1; transform: translateY(0); } }
                    .animate-fade-in { animation: fade-in 1s cubic-bezier(.4,0,.2,1) both; }
                    .delay-100 { animation-delay: 0.1s; }
                    .delay-200 { animation-delay: 0.2s; }
                    .delay-300 { animation-delay: 0.3s; }
                    .delay-400 { animation-delay: 0.4s; }
                    .delay-500 { animation-delay: 0.5s; }
                    .delay-600 { animation-delay: 0.6s; }
                    @keyframes float-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-16px); } }
                    .animate-float-slow { animation: float-slow 7s ease-in-out infinite; }
                    @keyframes float-medium { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-24px); } }
                    .animate-float-medium { animation: float-medium 5s ease-in-out infinite; }
                `}</style>
            </section>
            {/* Animated Features Section */}
            {/* <section className="w-full bg-white px-4 py-16">
        <div className="mx-auto flex w-full max-w-5xl flex-wrap justify-center gap-8">
          <h2 className="mb-8 w-full text-center font-ubuntu text-3xl font-bold text-congress-blue md:text-4xl">Our Features & Values</h2> */}
            {/* Animated reveal, icon above text, card hover */}
            {/* <AnimatedFeatures /> */}
            {/* </div>
      </section> */}
            {/* Our Story Section */}
            <section className="w-full bg-gradient-to-br from-teal-50 to-blue-100 px-4 py-16">
                <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-12 md:flex-row">
                    {/* Text Side */}
                    <div className="flex flex-1 flex-col items-center md:items-start">
                        <h2 className="mb-4 font-ubuntu text-3xl font-bold text-lochmara-blue md:text-4xl">Our Story: Born from Purpose, Built for Impact</h2>
                        <p className="mb-4 font-redhat text-lg text-gray-700">PUREMEDIFY LIMITED was founded to bridge the gap between medical professionals and the communities they serve. What started as a passion for health equity has evolved into a bold, cross-sectoral enterprise serving diverse communities across healthcare, agriculture, logistics, energy, technology, real estate, and lifestyle.</p>
                        <Link to="/about" className="mt-4 inline-block rounded-full bg-brand px-6 py-3 font-ubuntu font-semibold text-white shadow transition hover:bg-brand-green">Read Our Story</Link>
                    </div>
                    {/* Image Side */}
                    <div className="h-64 w-full flex-1 md:h-96">
                        <img src={process.env.PUBLIC_URL + '/image/puremedifycom.webp'} alt="Our Story" className="h-full w-full rounded-xl object-cover shadow-lg" />
                    </div>
                </div>
            </section>
            {/* What We Do / Services Overview */}
            <section className="w-full bg-white px-4 py-16">
                <div className="mx-auto w-full max-w-5xl">
                    <h2 className="mb-12 text-center font-ubuntu text-3xl font-bold text-congress-blue md:text-4xl">What We Do</h2>
                    <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                        {/* Card 1 */}
                        <div className="group flex cursor-pointer flex-col items-center rounded-2xl bg-gradient-to-br from-blue-50 to-white p-8 text-center shadow-lg transition-transform duration-300 hover:-translate-y-2 hover:shadow-2xl">
                            <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-lochmara-blue/10 transition-all duration-300 group-hover:bg-lochmara-blue/20">
                                <svg
                                    className="h-10 w-10 text-lochmara-blue transition-transform duration-300 group-hover:scale-110"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M9 17v-2a4 4 0 018 0v2M5 10a7 7 0 0114 0v2a7 7 0 01-14 0v-2z"
                                    />
                                </svg>
                            </span>

                            <h3 className="mb-2 font-ubuntu text-xl font-bold text-lochmara-blue">
                                Medical & Community Healthcare Services
                            </h3>

                            <p className="mb-2 font-redhat text-gray-600">
                                Outpatient & Inpatient Care, Telemedicine, Mobile Clinics, Labs, Elderly Care, Supplies, Training.
                            </p>

                        <Link
                            to="/great-emmanuel-hospital"
                            className="animate-pulse bg-gradient-to-r from-lochmara-blue to-cyan-500 bg-clip-text text-xl font-bold text-transparent transition duration-300 hover:scale-105"
                        >
                            Through The Great Emmanuel Hospital
                        </Link>
                        </div>

                        {/* Card 2 */}
                        <div className="group flex cursor-pointer flex-col items-center rounded-2xl bg-gradient-to-br from-green-50 to-white p-8 text-center shadow-lg transition-transform duration-300 hover:-translate-y-2 hover:shadow-2xl">
                            <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-malachite-green/10 transition-all duration-300 group-hover:bg-malachite-green/20">
                                <svg className="h-10 w-10 text-malachite-green transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 1.343-3 3 0 1.657 1.343 3 3 3s3-1.343 3-3c0-1.657-1.343-3-3-3zm0 0V4m0 7v9m0 0H7m5 0h5" /></svg>
                            </span>
                            <h3 className="mb-2 font-ubuntu text-xl font-bold text-malachite-green">Lifestyle & Wellness</h3>
                            <p className="mb-2 font-redhat text-gray-600">Organic Cosmetics, Beauty, Spa, Grocery, Wellness Products, Consumer Services.</p>
                            <p className="relative inline-block from-brand to-pink-500 text-xl font-bold text-congress-blue before:absolute before:-bottom-1 before:left-0 before:h-1 before:w-0 before:bg-gradient-to-r before:transition-all before:duration-500 hover:scale-105 hover:before:w-full">
                                Through Our Marketplace
                            </p>
                        </div>
                        {/* Card 3 */}
                        <div className="group flex cursor-pointer flex-col items-center rounded-2xl bg-gradient-to-br from-blue-50 to-white p-8 text-center shadow-lg transition-transform duration-300 hover:-translate-y-2 hover:shadow-2xl">
                            <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand/10 transition-all duration-300 group-hover:bg-brand/20">
                                <svg
                                    className="h-10 w-10 text-brand transition-transform duration-300 group-hover:scale-110"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </span>

                            <h3 className="mb-2 font-ubuntu text-xl font-bold text-brand">
                                Charity & Social Impact
                            </h3>

                            <p className="mb-2 font-redhat text-gray-600">
                                PureMedify Foundation: Serving underserved communities, women, children, and people living with Autism.
                            </p>

                            <p className="animate-pulse bg-gradient-to-r from-brand to-pink-500 bg-clip-text text-xl font-bold text-transparent transition duration-300 hover:scale-105">
                                Through PureMedify Foundation
                            </p>
                        </div>

                        {/* Card 4 */}
                        {/* <div className="group flex cursor-pointer flex-col items-center rounded-2xl bg-gradient-to-br from-blue-50 to-white p-8 text-center shadow-lg transition-transform duration-300 hover:-translate-y-2 hover:shadow-2xl">
              <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-congress-blue/10 transition-all duration-300 group-hover:bg-congress-blue/20">
                <svg className="h-10 w-10 text-congress-blue transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" /></svg>
              </span>
              <h3 className="mb-2 font-ubuntu text-xl font-bold text-congress-blue">Technology & Logistics</h3>
              <p className="mb-2 font-redhat text-gray-600">Digital Solutions, eCommerce, Logistics, Infrastructure, Construction, Energy, Automotive.</p>
            </div> */}
                        {/* Card 5 */}
                        {/* <div className="group flex cursor-pointer flex-col items-center rounded-2xl bg-gradient-to-br from-green-50 to-white p-8 text-center shadow-lg transition-transform duration-300 hover:-translate-y-2 hover:shadow-2xl">
              <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-malachite-green/10 transition-all duration-300 group-hover:bg-malachite-green/20">
                <svg className="h-10 w-10 text-malachite-green transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 1.343-3 3 0 1.657 1.343 3 3 3s3-1.343 3-3c0-1.657-1.343-3-3-3zm0 0V4m0 7v9m0 0H7m5 0h5" /></svg>
              </span>
              <h3 className="mb-2 font-ubuntu text-xl font-bold text-malachite-green">Agro-Allied & Food Security</h3>
              <p className="mb-2 font-redhat text-gray-600">Supporting food security and sustainable agriculture for thriving communities.</p>
            </div> */}
                    </div>
                    <div className="mt-12 flex justify-center">
                        <Link to="/services" className="rounded-full bg-brand px-8 py-4 font-ubuntu text-lg font-semibold text-white shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-brand-green">See All Services</Link>
                    </div>
                </div>
            </section>
            {/* Community Impact Section */}
            <section className="w-full bg-gradient-to-r from-blue-50 to-teal-100 px-4 py-16">
                <div className="mx-auto w-full max-w-5xl">
                    <h2 className="mb-8 text-center font-ubuntu text-3xl font-bold text-lochmara-blue md:text-4xl">Community Impact</h2>
                    <div className="grid w-full grid-cols-1 gap-8 md:grid-cols-2">
                        <div className="flex flex-col rounded-lg bg-white p-6 shadow">
                            <h3 className="mb-2 font-ubuntu text-xl font-bold text-malachite-green">Health Education</h3>
                            <p className="mb-2 font-redhat text-gray-700">Over 5,000 people reached through our health education campaigns.</p>
                            <span className="font-redhat text-xs text-gray-400">2023-2024</span>
                        </div>
                        <div className="flex flex-col rounded-lg bg-white p-6 shadow">
                            <h3 className="mb-2 font-ubuntu text-xl font-bold text-malachite-green">Women Empowerment</h3>
                            <p className="mb-2 font-redhat text-gray-700">Empowering women in rural areas with skills and support.</p>
                            <span className="font-redhat text-xs text-gray-400">Ongoing</span>
                        </div>
                    </div>
                    <div className="mt-8 flex justify-center">
                        <Link to="/community" className="rounded-full bg-brand px-6 py-3 font-ubuntu text-base font-semibold text-white shadow transition hover:bg-brand-green">See Our Impact</Link>
                    </div>
                </div>
            </section>
            {/* Partnerships & Projects Section */}
            <section className="w-full bg-white px-4 py-16">
                <div className="mx-auto w-full max-w-5xl">
                    <h2 className="mb-8 text-center font-ubuntu text-3xl font-bold text-congress-blue md:text-4xl">Partnerships & Projects</h2>
                    <div className="grid w-full grid-cols-1 gap-8 md:grid-cols-2">
                        <div className="flex flex-col rounded-lg bg-white p-6 shadow">
                            <h3 className="mb-2 font-ubuntu text-xl font-bold text-malachite-green">NGO Collaboration</h3>
                            <p className="mb-2 font-redhat text-gray-700">Partnered with Health4All to deliver vaccines to remote communities.</p>
                            <span className="font-redhat text-xs text-gray-400">Ongoing</span>
                        </div>
                        <div className="flex flex-col rounded-lg bg-white p-6 shadow">
                            <h3 className="mb-2 font-ubuntu text-xl font-bold text-malachite-green">Government Project</h3>
                            <p className="mb-2 font-redhat text-gray-700">Working with Abia State to improve rural healthcare infrastructure.</p>
                            <span className="font-redhat text-xs text-gray-400">2024</span>
                        </div>
                    </div>
                    <div className="mt-8 flex justify-center">
                        <Link to="/partnerships" className="rounded-full bg-brand px-6 py-3 font-ubuntu text-base font-semibold text-white shadow transition hover:bg-brand-green">See All Partnerships</Link>
                    </div>
                </div>
            </section>
            {/* Meet the Team Preview Section */}
            <section className="w-full bg-gradient-to-br from-teal-50 to-blue-100 px-4 py-16">
                <div className="mx-auto w-full max-w-5xl">
                    <h2 className="mb-8 text-center font-ubuntu text-3xl font-bold text-lochmara-blue md:text-4xl">Meet the Team</h2>
                    <div className="grid w-full grid-cols-1 gap-8 md:grid-cols-3">
                        <div className="flex flex-col items-center rounded-lg bg-white p-6 shadow-lg">
                            <img
                                src="/image/david.jpeg"
                                className="mb-4 h-24 w-24 rounded-full"
                                alt="Pure medify Team Member"
                            />
                            <h3 className="font-ubuntu text-lg font-bold text-congress-blue">
                                David I. Mgbeke
                            </h3>
                            <p className="font-redhat text-gray-500">
                                Founder and Manager
                            </p>
                            <Link
                                to="/great-emmanuel-hospital"
                                className="inline-block animate-pulse bg-gradient-to-r from-congress-blue to-cyan-500 bg-clip-text font-bold text-transparent transition duration-300 hover:scale-105"
                            >
                                The Great Emmanuel Hospital
                            </Link>
                        </div>
                        <div className="flex flex-col items-center rounded-lg bg-white p-6 shadow-lg">
                            <img src="/image/dremma.jpeg" className="mb-4 h-24 w-24 rounded-full" alt="Pure medify Team Member" />
                            <h3 className="font-ubuntu text-lg font-bold text-congress-blue">Dr Emmanuel Odinaka</h3>
                            <p className="font-redhat text-gray-500">Founder & CEO</p>
                        </div>
                        <div className="flex flex-col items-center rounded-lg bg-white p-6 shadow-lg">
                            <img src="/image/fortunatus.jpeg" className="mb-4 h-24 w-24 rounded-full" alt="Pure medify Team Member" />
                            <h3 className="font-ubuntu text-lg font-bold text-congress-blue">Dr Fortunatus Obi</h3>
                            <p className="font-redhat text-gray-500">COO</p>
                        </div>
                        <div className="flex flex-col items-center rounded-lg bg-white p-6 shadow-lg">
                            <img src="/image/lawrence.png" className="mb-4 h-24 w-24 rounded-full" alt="Pure medify Team Member" />
                            <h3 className="font-ubuntu text-lg font-bold text-congress-blue">Ikechukwu Lawrence </h3>
                            <p className="font-redhat text-gray-500">Director of Business and Logistics</p>
                        </div>
                        <div className="flex flex-col items-center rounded-lg bg-white p-6 shadow-lg">
                            <img src="/image/daberechi.png" className="mb-4 h-24 w-24 rounded-full" alt="Pure medify Team Member" />
                            <h3 className="font-ubuntu text-lg font-bold text-congress-blue">Arch. Samuel Daberechi</h3>
                            <p className="font-redhat text-gray-500">Director of Works and Maintenance</p>
                        </div>

                        <div className="flex flex-col items-center rounded-lg bg-white p-6 shadow-lg">
                            <img src="/image/onyinyechi.jpeg" className="mb-4 h-24 w-24 rounded-full" alt="Pure medify Team Member" />
                            <h3 className="font-ubuntu text-lg font-bold text-congress-blue">Onyinyechi Patience</h3>
                            <p className="font-redhat text-gray-500">Director of Finance & Secretary</p>
                        </div>


                    </div>
                    <div className="mt-8 flex justify-center">
                        <Link to="/team" className="rounded-full bg-brand px-6 py-3 font-ubuntu text-base font-semibold text-white shadow transition hover:bg-brand-green">Meet the Full Team</Link>
                    </div>
                </div>
            </section>

            {/* Call to Action Section */}
                        <section className="flex w-full flex-col items-center bg-gradient-to-br from-blue-100 to-teal-50 px-4 py-12">
                            <h2 className="mb-4 text-center font-ubuntu text-2xl font-bold text-congress-blue md:text-3xl">Take the Next Step</h2>
                            <p className="mb-6 max-w-2xl text-center font-redhat text-lg text-gray-700">Let’s work together to build healthier communities.
            Whether you need medical support, healthcare training, digital solutions, or partnership opportunities, PUREMEDIFY is ready to collaborate.</p>
                            
                        </section>
            {/* Careers CTA Section */}
            <section className="w-full bg-white px-4 py-16">
                <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-12 md:flex-row">
                    <div className="flex flex-1 flex-col items-center md:items-start">
                        <h2 className="mb-4 font-ubuntu text-3xl font-bold text-congress-blue md:text-4xl">Join Our Team</h2>
                        <p className="mb-4 font-redhat text-lg text-gray-700">Ready to make a difference? Explore career opportunities and help us create a healthier, more equitable world.</p>
                        <Link to="/careers" className="mt-4 inline-block rounded-full bg-brand px-6 py-3 font-ubuntu font-semibold text-white shadow transition hover:bg-brand-green">View Careers</Link>
                    </div>
                    <div className="flex flex-1 justify-center">
                        <svg className="h-32 w-32 text-brand" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    </div>
                </div>
            </section>
            {/* Contact/Connect Section */}
            <section className="w-full bg-gradient-to-br from-blue-100 to-teal-50 px-4 py-16">
                <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-12 md:flex-row">
                    <div className="flex flex-1 flex-col items-center md:items-start">
                        <h2 className="mb-4 font-ubuntu text-3xl font-bold text-lochmara-blue md:text-4xl">Contact & Connect</h2>
                        <p className="mb-4 font-redhat text-lg text-gray-700">Let’s create a meaningful impact together. Whether you're a potential partner, client, or looking for support, our team is ready to listen.</p>
                        <ul className="mb-4 font-redhat text-base text-gray-600">
                            <li><b>Corporate Address:</b> Maneja House, 10 Mbato Road, Ngodo Isuochi, Umunneochi LGA, Abia State.</li>
                            <li><b>Email:</b> info@puremedify.com</li>
                            <li><b>Phone:</b> +234 7035855151</li>
                            <li><b>Website:</b> www.puremedify.com</li>
                        </ul>
                        <Link to="/contact" className="mt-4 inline-block rounded-full bg-lochmara-blue px-6 py-3 font-ubuntu font-semibold text-white shadow transition hover:bg-malachite-green">Contact Us</Link>
                    </div>
                    <div className="flex flex-1 justify-center">
                        <svg className="h-32 w-32 text-brand" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 10.5a8.38 8.38 0 01-.9 3.8c-.5 1-1.1 2-2 2.7-.8.7-1.8 1.2-2.8 1.5-1.1.3-2.2.5-3.3.5s-2.2-.2-3.3-.5c-1-.3-2-.8-2.8-1.5-.9-.7-1.5-1.7-2-2.7A8.38 8.38 0 013 10.5c0-1.1.2-2.2.5-3.3.3-1 .8-2 1.5-2.8.7-.9 1.7-1.5 2.7-2 .9-.5 2-.7 3.3-.7s2.2.2 3.3.7c1 .5 2 1.1 2.7 2 .7.8 1.2 1.8 1.5 2.8.3 1.1.5 2.2.5 3.3z" /></svg>
                    </div>
                </div>
            </section>
        </div>
    </>
);

export default Home;
